import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
} from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { BehaviorSubject, combineLatest, map, Subject, tap } from "rxjs";
import { CONSTANT } from "src/app/constant";
import { Action, OBAttempt, RowVM } from "src/app/models/models";
import { CbtaModalComponent } from "src/app/shared/components/cbta-modal/cbta-modal.component";

@Component({
	selector: "app-observation-card-v2",
	templateUrl: "./observation-card.component.html",
	styleUrls: ["./observation-card.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ObservationCardV2Component implements OnInit, OnChanges, OnDestroy {
	readonly MAX = CONSTANT.MAX_OB_ATTEMPT_LIMIT;
	readonly OB_LAST_ACTION = CONSTANT.OB_LAST_ACTION;

	@Input() obFormGroup!: UntypedFormGroup;
	@Input() OB!: { title: string; item_desc: string[] };
	@Input() showButtons: boolean = true;
	@Input() enableNA: boolean = false;
	@Input() singleCount: boolean = false;
	/** Per OB control name — true when the latest change can be undone one step. */
	@Input() canUndoByKey: Record<string, boolean> = {};

	@Output() emitOBContainerValue = new EventEmitter<any>();

	private formValue$ = new BehaviorSubject<any>({});
	private canUndoByKey$ = new BehaviorSubject<Record<string, boolean>>({});
	private actions$ = new Subject<Action>();

	// Grey state (per-card)
	private greySet = new Set<number>();
	isGreyed(index: number): boolean {
		return this.greySet.has(index);
	}

	// Remember the lastAction before greying so we can restore it
	private prevLastAction = new Map<number, string>(); // index -> lastAction before grey

	// Long-press support
	private longPressMs = 2000;
	private longPressTimers = new Map<number, number>();

	// Flag to prevent multiple dialog opens (fixes iPad blue screen issue)
	private isDialogOpen = false;

	vm$ = combineLatest([this.formValue$, this.canUndoByKey$]).pipe(
		map(([, canUndoByKey]) => {
			const count = Array.isArray(this.OB?.item_desc) ? this.OB.item_desc.length : 0;
			const rows: RowVM[] = Array.from({ length: count }, (_row: RowVM, i) => {
				const key = this.ctrlKey(i);
				const ctrl = this.obFormGroup?.get(key);

				const ob = (ctrl?.value as OBAttempt) ?? {
					totalCount: 0,
					currentCount: 0,
					preDefined: false,
					isDisabled: false,
					lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
				};

				if (ob.isDisabled == true) {
					this.greySet.add(i);
				} else {
					this.greySet.delete(i);
				}

				return {
					ob,
					disabledAdd: this.computeDisableAdd(ob),
					disabledRemove: this.computeDisableRemove(ob),
					title: this.prettyTitle(i),
					key,
					showUndo: !!canUndoByKey[key],
				} as RowVM;
			});

			return { rows };
		}),
	);

	constructor(
		private cdr: ChangeDetectorRef,
		private dialog: MatDialog,
	) {}

	ngOnInit(): void {
		this.canUndoByKey$.next(this.canUndoByKey ?? {});

		if (this.obFormGroup) {
			this.formValue$.next(this.obFormGroup.value);
			this.obFormGroup.valueChanges.subscribe((val) => this.formValue$.next(val));
		}

		const isFromNAEvent = false;
		const isDisabled = false;

		// Add / Remove actions (blocked while grey)
		this.actions$
			.pipe(
				tap((action) => {
					if (!action || !this.obFormGroup) return;

					const i = action.index;
					if (this.isGreyed(i)) return; // disabled while greyed

					const key = this.ctrlKey(i);
					const ctrl = this.obFormGroup.get(key);
					const val = ctrl?.value as OBAttempt | undefined;
					if (!ctrl || !val) return;

					if (action.type === "RESET") {
						this.applyReset(action.index);
						return;
					}

					if (action.type === "ADD") {
						if (this.computeDisableAdd(val)) return;
						let { currentCount, totalCount } = val;
						if (val.preDefined || totalCount < this.MAX) {
							totalCount++;
							currentCount++;
						}
						const updated: OBAttempt = {
							...val,
							totalCount,
							currentCount,
							lastAction: this.OB_LAST_ACTION.ADDED,
						};
						this.emitOBContainerValue.emit([ctrl, key, true, updated, isDisabled, isFromNAEvent]);
					}

					if (action.type === "REMOVE") {
						if (this.computeDisableRemove(val)) return;
						let { currentCount, totalCount } = val;
						let last = val.lastAction ?? "-";
						if (val.preDefined) {
							if (currentCount > 0) {
								currentCount--;
								last = this.OB_LAST_ACTION.REMOVED;
							} else if (totalCount < this.MAX) {
								totalCount++;
								last = this.OB_LAST_ACTION.REMOVED;
							}
						} else {
							if (currentCount > 0) {
								if (totalCount < this.MAX) {
									// Convert an observed attempt into a new unobserved attempt,
									// so 1/3 becomes 1/4 instead of 0/3.
									totalCount++;
								} else {
									currentCount--;
								}
								last = this.OB_LAST_ACTION.REMOVED;
							} else if (totalCount < this.MAX) {
								totalCount++;
								last = this.OB_LAST_ACTION.REMOVED;
							}
						}
						const updated: OBAttempt = {
							...val,
							totalCount,
							currentCount,
							lastAction: last,
						};

						this.emitOBContainerValue.emit([ctrl, key, false, updated, isDisabled, isFromNAEvent]);
					}
				}),
			)
			.subscribe();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes["canUndoByKey"]) {
			this.canUndoByKey$.next(this.canUndoByKey ?? {});
		}
		if (changes["obFormGroup"] && this.obFormGroup) {
			this.formValue$.next(this.obFormGroup.value);
		}
	}

	ngOnDestroy(): void {
		this.longPressTimers.forEach((id) => window.clearTimeout(id));
		this.longPressTimers.clear();
	}

	// Add / Remove triggers
	add(index: number) {
		this.actions$.next({ type: "ADD", index });
	}
	remove(index: number) {
		this.actions$.next({ type: "REMOVE", index });
	}
	reset(index: number) {
		if (this.isGreyed(index)) return;
		this.confirmReset(index);
	}

	private confirmReset(index: number): void {
		if (this.isDialogOpen) return;

		try {
			this.isDialogOpen = true;
			const dialogRef = this.dialog.open(CbtaModalComponent, {
				panelClass: "logout-modal",
				height: "200px",
				width: "360px",
				hasBackdrop: true,
				data: {
					modalTitle: "Confirmation",
					modalContent: "Are you sure to undo the last observation change?",
					modalBtnConfirmText: "Yes",
					modalBtnCancelText: "No",
				},
			});

			const sub = dialogRef.afterClosed().subscribe((result: any) => {
				this.isDialogOpen = false;
				if (result?.close) {
					this.actions$.next({ type: "RESET", index });
				}
				sub.unsubscribe();
			});
		} catch (error) {
			console.error("Error opening reset dialog:", error);
			this.isDialogOpen = false;
		}
	}

	private applyReset(index: number): void {
		const key = this.ctrlKey(index);
		const ctrl = this.obFormGroup?.get(key);
		const current = ctrl?.value as OBAttempt | undefined;
		if (!ctrl || !current) return;

		this.greySet.delete(index);
		this.prevLastAction.delete(index);

		this.emitOBContainerValue.emit([ctrl, key, null, current, false, false, true]);
	}

	// ===== Toggle grey via long-press/right-click WITH CONFIRM =====
	onCardPointerDown(ev: PointerEvent, index: number, row: RowVM): void {
		if (ev.button !== 0) return; // only primary/touch
		if (this.isFromInteractive(ev)) return;

		// start 2s timer -> open confirm dialog when it fires
		const id = window.setTimeout(() => {
			this.clearTimer(index);
			this.confirmToggleGrey(index, row);
		}, this.longPressMs);
		this.longPressTimers.set(index, id);
	}

	onCardPointerUp(_ev: PointerEvent, index: number): void {
		this.clearTimer(index);
	}
	onCardPointerCancel(_ev: PointerEvent, index: number): void {
		this.clearTimer(index);
	}

	onCardContextMenu(ev: MouseEvent | TouchEvent, index: number, row: RowVM): void {
		ev.preventDefault(); // suppress context menu
		ev.stopPropagation(); // prevent event bubbling (important for iPad)
		this.clearTimer(index);

		// Prevent multiple dialog opens
		if (this.isDialogOpen) {
			return;
		}

		this.confirmToggleGrey(index, row); // confirm on right-click as well
	}

	private confirmToggleGrey(index: number, row: RowVM): void {
		if (!row.ob.preDefined && row.ob.lastAction == this.OB_LAST_ACTION.NO_ACTION && !(row.ob.totalCount > 0)) return;

		// Prevent multiple dialog opens
		if (this.isDialogOpen) {
			return;
		}

		const willGrey = !this.isGreyed(index);
		const message = willGrey ? "Are you sure to mark this OB as N/A?" : "Enable this card again?";

		try {
			this.isDialogOpen = true;

			const dialogRef = this.dialog.open(CbtaModalComponent, {
				panelClass: "logout-modal",
				height: "200px",
				width: "360px",
				hasBackdrop: true,
				data: {
					modalTitle: "Confirmation",
					modalContent: message,
					modalBtnConfirmText: "Yes",
					modalBtnCancelText: "No",
				},
			});

			const sub = dialogRef.afterClosed().subscribe((result: any) => {
				this.isDialogOpen = false; // Reset flag when dialog closes
				if (result?.close) {
					this.toggleGrey(index, row);
				}
				sub.unsubscribe();
			});
		} catch (error) {
			console.error("Error opening dialog:", error);
			this.isDialogOpen = false; // Reset flag on error
		}
	}

	private toggleGrey(index: number, row?: RowVM) {
		const greying = !this.greySet.has(index);
		if (greying) {
			this.greySet.add(index);
		} else {
			this.greySet.delete(index);
		}

		// Emit like add/remove with updated lastAction
		const key = row?.key ?? this.ctrlKey(index);
		const ctrl = this.obFormGroup?.get(key);
		const val = ctrl?.value as OBAttempt | undefined;
		const isFromNAEvent = true;

		if (ctrl && val) {
			if (greying) {
				// save current lastAction and emit DISABLED
				this.prevLastAction.set(index, val.lastAction ?? this.OB_LAST_ACTION.NO_ACTION);
				const isDisabled = true;
				const updated: OBAttempt = { ...val, isDisabled, lastAction: (this.OB_LAST_ACTION as any).DISABLED };
				if (val.lastAction == this.OB_LAST_ACTION.ADDED) {
					this.emitOBContainerValue.emit([ctrl, key, true, updated, isDisabled, isFromNAEvent]); // isGrey=true
				} else if (val.lastAction == this.OB_LAST_ACTION.REMOVED) {
					this.emitOBContainerValue.emit([ctrl, key, false, updated, isDisabled, isFromNAEvent]); // isGrey=true
				} else {
					this.emitOBContainerValue.emit([ctrl, key, null, updated, isDisabled, isFromNAEvent]); // isGrey=true
				}
			} else {
				// restore previous lastAction
				const restore = this.prevLastAction.get(index) ?? "-";
				this.prevLastAction.delete(index);
				const isDisabled = false;
				const updated: OBAttempt = { ...val, isDisabled, lastAction: (this.OB_LAST_ACTION as any).RESTORE };
				if (val.lastAction == this.OB_LAST_ACTION.ADDED) {
					this.emitOBContainerValue.emit([ctrl, key, true, updated, isDisabled, isFromNAEvent]); // isGrey=true
				} else if (val.lastAction == this.OB_LAST_ACTION.REMOVED) {
					this.emitOBContainerValue.emit([ctrl, key, false, updated, isDisabled, isFromNAEvent]); // isGrey=true
				} else {
					this.emitOBContainerValue.emit([ctrl, key, null, updated, isDisabled, isFromNAEvent]); // isGrey=true
				}
			}
		}

		this.cdr.markForCheck();
	}

	private clearTimer(index: number): void {
		const id = this.longPressTimers.get(index);
		if (id != null) {
			window.clearTimeout(id);
			this.longPressTimers.delete(index);
		}
	}

	private isFromInteractive(ev: Event): boolean {
		const el = ev.target as HTMLElement | null;
		if (!el) return false;
		return !!el.closest("button, [role='button'], a, input, textarea, select, .attemptBtn, .resetBtn");
	}

	// Helpers
	private prettyTitle(index: number): string {
		return `${this.OB?.title ?? ""}${index + 1}`;
	}
	private ctrlKey(index: number): string {
		const withSpace = `${this.OB?.title ?? ""} ${index + 1}`;
		if (this.obFormGroup?.get(withSpace)) return withSpace;
		return `${this.OB?.title ?? ""}${index + 1}`;
	}
	private computeDisableAdd(ob: OBAttempt): boolean {
		if (!ob) return true;
		if (ob.preDefined) return false;
		if (ob.totalCount >= this.MAX) return true;
		return false;
	}
	private computeDisableRemove(ob: OBAttempt): boolean {
		if (!ob) return true;
		if (ob.currentCount > 0) return false;
		if (ob.totalCount >= this.MAX) return true;
		// Non-predefined OBs start at 0/0; minus must still apply a first "unobserved"
		// step (denominator only) to reach 0/1, matching legacy REMOVE behavior.
		if (ob.preDefined && ob.currentCount === 0 && ob.totalCount === 0) return true;
		return false;
	}
}
